import { useEffect, useState } from 'preact/hooks'
import { AtSign, Globe } from 'lucide-preact'
import { SegmentedToggle } from '@/components/common/SegmentedToggle'
import { SuppressionList } from './SuppressionList'
import { DomainSuppressionList } from './DomainSuppressionList'

type Tab = 'addresses' | 'domains'

/**
 * Company-admin suppression screen. One home for the two do-not-contact lists: individual addresses
 * (opt-outs, complaints, uploaded lists) and whole domains (competitor / do-not-contact lists). Each tab
 * owns its own data hook; both stay mounted so their live totals feed the toggle counts and switching is
 * instant. The shared header + toggle live here; each list renders its own actions, search, and table.
 */
export function SuppressionScreen({ clientCompanyId }: { clientCompanyId: string }) {
  const [tab, setTab] = useState<Tab>('addresses')
  const [addressTotal, setAddressTotal] = useState<number | null>(null)
  const [domainTotal, setDomainTotal] = useState<number | null>(null)

  // Clear the tab counts when the company changes so the badges never show the previous company's totals
  // while each tab's hook refetches (or if that refetch errors). Each tab re-reports its total on load.
  useEffect(() => {
    setAddressTotal(null)
    setDomainTotal(null)
  }, [clientCompanyId])

  const countBadge = (total: number | null) =>
    total === null ? undefined : (
      <span className="ml-0.5 rounded-full bg-slate-200 dark:bg-slate-700 px-1.5 py-0.5 text-[11px] font-semibold text-slate-600 dark:text-slate-300 tabular-nums">
        {total}
      </span>
    )

  return (
    <div className="px-6 py-4 max-w-7xl mx-auto space-y-6">
      <div>
        <h2 className="text-xl font-semibold tracking-tight text-slate-900 dark:text-white">Suppression</h2>
        <p className="text-sm mt-1.5 text-slate-500 dark:text-slate-400 max-w-prose leading-relaxed">
          People and companies this workspace will never send marketing email to. Applies company-wide,
          across every workspace and campaign.
        </p>
      </div>

      <SegmentedToggle<Tab>
        ariaLabel="Suppression view"
        value={tab}
        onChange={setTab}
        options={[
          { value: 'addresses', label: 'Addresses', icon: <AtSign className="w-4 h-4" />, badge: countBadge(addressTotal) },
          { value: 'domains', label: 'Domains', icon: <Globe className="w-4 h-4" />, badge: countBadge(domainTotal) },
        ]}
      />

      <div hidden={tab !== 'addresses'}>
        <SuppressionList clientCompanyId={clientCompanyId} onTotalChange={setAddressTotal} />
      </div>
      <div hidden={tab !== 'domains'}>
        <DomainSuppressionList clientCompanyId={clientCompanyId} onTotalChange={setDomainTotal} />
      </div>
    </div>
  )
}
